import { useMemo, useState } from 'react'
import { Plus, Wrench, Tag } from '@/ui/icons'
import { Badge, Button, Card } from '@/ui/components'
import { PageLayout } from '@/ui/layout'
import { cn } from '@/shared/utils'
import { garageVehiclesMock } from './garageMockData'

export function GaragePage() {
  const [selectedVehicleId, setSelectedVehicleId] = useState(garageVehiclesMock[0]?.id ?? '')

  const selectedVehicle = useMemo(
    () => garageVehiclesMock.find((vehicle) => vehicle.id === selectedVehicleId) ?? garageVehiclesMock[0],
    [selectedVehicleId]
  )

  if (!selectedVehicle) {
    return (
      <PageLayout>
        <div className="flex h-full min-h-[60vh] items-center justify-center px-6">
          <Card className="w-full max-w-lg border-border bg-card p-8 text-center">
            <p className="text-sm text-muted-foreground">Garage preview is being prepared.</p>
          </Card>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <div className="h-full min-h-[calc(100dvh-4rem)] w-full bg-background">
        <div className="mx-auto flex h-full w-full max-w-[1500px] flex-col gap-4 px-3 py-4 md:flex-row md:gap-6 md:px-6">
          <aside className="w-full md:w-[280px] md:flex-shrink-0">
            <Card className="h-full border-border bg-card/95 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">My Garage</h2>
                <Button variant="ghost" size="icon" disabled aria-label="Add vehicle coming soon">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {garageVehiclesMock.map((vehicle) => {
                  const isSelected = vehicle.id === selectedVehicle.id
                  return (
                    <button
                      key={vehicle.id}
                      type="button"
                      onClick={() => setSelectedVehicleId(vehicle.id)}
                      className={cn(
                        'w-full rounded-lg border p-3 text-left transition-colors',
                        isSelected
                          ? 'border-primary bg-adventure-100/35'
                          : 'border-border bg-background hover:border-adventure-300'
                      )}
                    >
                      <p className="truncate text-sm font-semibold text-foreground">{vehicle.name}</p>
                      <p className="mt-1 truncate text-xs text-muted-foreground">{vehicle.subtitle}</p>
                      <p className="mt-2 text-xs text-muted-foreground">{vehicle.modCount} mods</p>
                    </button>
                  )
                })}
              </div>
            </Card>
          </aside>

          <section className="min-w-0 flex-1 space-y-4 pb-20 md:pb-6">
            <div className="flex items-center justify-between gap-3">
              <Badge variant="outline" className="border-adventure-400/60 text-adventure-700 dark:text-adventure-300">
                Coming Soon Preview
              </Badge>
            </div>

            <Card className="overflow-hidden border-border bg-card">
              <div className={cn('relative h-48 w-full bg-gradient-to-r md:h-56', selectedVehicle.coverClassName)}>
                <div className="absolute inset-0 bg-earth-900/30" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="text-2xl font-bold text-primary-foreground">{selectedVehicle.name}</p>
                  <p className="text-sm text-primary-foreground/90">{selectedVehicle.subtitle}</p>
                </div>
              </div>
            </Card>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Card className="border-border bg-card p-5">
                <p className="text-3xl font-bold text-foreground">{selectedVehicle.modCount}</p>
                <p className="text-xs text-muted-foreground">Mods</p>
              </Card>
              <Card className="border-border bg-card p-5">
                <p className="text-3xl font-bold text-foreground">{selectedVehicle.categoryCount}</p>
                <p className="text-xs text-muted-foreground">Categories</p>
              </Card>
            </div>

            <Card className="border-border bg-card p-5">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">Build List</h3>
                <Button size="sm" disabled className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Mod
                </Button>
              </div>

              <div className="space-y-4">
                {selectedVehicle.buildList.map((category) => (
                  <div key={category.id}>
                    <p className="mb-2 text-xs font-semibold text-muted-foreground">
                      {category.label}
                    </p>
                    <div className="space-y-2">
                      {category.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2"
                        >
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">{item.name}</p>
                            <p className="truncate text-xs text-muted-foreground">{item.brand}</p>
                          </div>
                          <div className="ml-2 flex items-center gap-2 text-muted-foreground">
                            <Tag className="h-4 w-4" />
                            <Wrench className="h-4 w-4" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </section>
        </div>
      </div>
    </PageLayout>
  )
}
